import React, { useState, useEffect } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { BrandLogo } from "@/components/BrandLogo";
import { PortalProvider, usePortal } from "@/pages/portal/PortalContext";
import { PortalTopNav } from "@/pages/portal/PortalTopNav";
import { PortalMobileTopBar, PortalBottomNav, PortalMoreSheet } from "@/pages/portal/PortalMobile";
import PortalOverview from "@/pages/portal/PortalOverview";
import PortalMyTour from "@/pages/portal/PortalMyTour";
import PortalRouting from "@/pages/portal/PortalRouting";
import PortalQuote from "@/pages/portal/PortalQuote";
import PortalBilling from "@/pages/portal/PortalBilling";
import PortalDocuments from "@/pages/portal/PortalDocuments";
import PortalPickupReview from "@/pages/portal/PortalPickupReview";

const PortalWorkspace = () => {
  const { loading, error } = usePortal();
  const [moreOpen, setMoreOpen] = useState(false);

  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center" style={{ background: "var(--bg-canvas)" }}>
        <div className="text-center">
          <BrandLogo surface="light" height={40} withOpsMarker={false} />
          <div className="mt-6 text-[13px]" style={{ color: "var(--fg-muted)" }}>Loading your portal…</div>
        </div>
      </div>
    );
  }

  if (error) {
    const isSessionError = error.includes("session has ended");
    return (
      <div className="min-h-screen grid place-items-center p-6" style={{ background: "var(--bg-canvas)" }}>
        <div className="max-w-md w-full rounded-md p-8 text-center"
             style={{ background: "var(--bg-surface)", border: "1px solid var(--border-default)" }}
             data-testid={isSessionError ? "portal-session-error" : "portal-load-error"}>
          <BrandLogo surface="light" height={40} withOpsMarker={false} />
          <div className="mt-6 text-[14px] font-semibold" style={{ color: "var(--fg-primary)" }}>
            {isSessionError ? "Your secure session has expired" : "Unable to load portal"}
          </div>
          <div className="mt-2 text-[13px]" style={{ color: "var(--fg-secondary)" }}>
            {error}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: "var(--bg-canvas)" }} data-testid="page-portal">
      <PortalTopNav />
      <PortalMobileTopBar onOpenMore={() => setMoreOpen(true)} />

      <main
        className="w-full px-4 md:px-6 lg:px-8 py-6 md:py-10"
        style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 5rem)" }}
      >
        <Routes>
          <Route index element={<Navigate to="/portal/overview" replace />} />
          <Route path="overview"  element={<PortalOverview />} />
          <Route path="tour"      element={<PortalMyTour />} />
          <Route path="routing"   element={<PortalRouting />} />
          <Route path="quotes"    element={<PortalQuote />} />
          <Route path="billing"   element={<PortalBilling />} />
          <Route path="documents" element={<PortalDocuments />} />
          <Route path="pickup-review" element={<PortalPickupReview />} />
          <Route path="*"         element={<Navigate to="/portal/overview" replace />} />
        </Routes>
      </main>

      <PortalBottomNav onOpenMore={() => setMoreOpen(true)} />
      <PortalMoreSheet open={moreOpen} onClose={() => setMoreOpen(false)} />
    </div>
  );
};

export default function ClientPortal() {
  const [bootstrapping, setBootstrapping] = useState(true);

  /* TOKEN BOOTSTRAP → AUTH STATE READY → PORTAL LOAD.
     Extract ?token= from URL, write to localStorage, strip param.
     PortalProvider does NOT mount until this effect completes,
     guaranteeing the token is readable before the first /portal/me fetch. */
  useEffect(() => {
    const url = new URL(window.location.href);
    const t = url.searchParams.get("token");
    if (t) {
      localStorage.setItem("aa_portal_token", t);
      url.searchParams.delete("token");
      window.history.replaceState(
        {},
        "",
        url.pathname + url.search + url.hash
      );
    }
    setBootstrapping(false);
  }, []);

  if (bootstrapping) {
    return (
      <div
        className="min-h-screen grid place-items-center"
        style={{ background: "var(--bg-canvas)" }}
        data-testid="portal-bootstrap"
      >
        <div className="text-center">
          <BrandLogo surface="light" height={40} withOpsMarker={false} />
          <div
            className="mt-6 text-[13px]"
            style={{ color: "var(--fg-muted)" }}
          >
            Authenticating…
          </div>
        </div>
      </div>
    );
  }

  return (
    <PortalProvider>
      <PortalWorkspace />
    </PortalProvider>
  );
}
